import { H4 } from '@blueprintjs/core';
import * as React from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { useAppSelector } from 'src/ui/app/hooks';
import { withConfirmationDs } from 'src/ui/components/withConfirmationDs';
import { noop } from 'src/ui/constants/empty';
import { BrowserAuthFailedWarning } from 'src/ui/containers/dashboard/BrowserAuthFailedWarning';
import { Dashboard } from 'src/ui/containers/dashboard/Dashboard';
import { DegradedWarning } from 'src/ui/containers/dashboard/DegradedWarning';
import { SSOWarning } from 'src/ui/containers/dashboard/SSOWarning';
import { UnauthorizedWarning } from 'src/ui/containers/dashboard/UnauthorizedWarning';
import { NewUserLanding } from 'src/ui/containers/landing/NewUserLanding';
import { Welcome } from 'src/ui/containers/landing/Welcome';
import { DEMO_CONNECTION_INDEX } from 'src/ui/features/connection';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import { useSavedConnectionsQuery } from 'src/ui/graph/types';
import {
  useCurrentConnection,
  useSetLastConnectionIndex,
} from 'src/ui/hooks/connection';
import { useLandingConnectionIdx } from 'src/ui/hooks/navbar/connection';
import { useQueryParams } from 'src/ui/hooks/query';
import {
  isBrowserAuthFailureSelector,
  isDegradedSelector,
  isUnauthorizedSelector,
  ssoErrorCodeSelector,
} from 'src/ui/selectors/connection';
import * as copy from 'src/ui/templates/copy';
import { buildRoute } from 'src/ui/utils/buildRoute';

export const LandingPage: React.VFC = () => {
  const isBrowserAuthFailure = useAppSelector(isBrowserAuthFailureSelector);
  const isDegraded = useAppSelector(isDegradedSelector);
  const isUnauthorized = useAppSelector(isUnauthorizedSelector);
  const ssoErrorCode = useAppSelector(ssoErrorCodeSelector);
  const history = useHistory();
  const { org = '' } = useParams<{ org?: string; id?: string }>();
  const queryParams = useQueryParams();

  const {
    data: connectionList,
    isLoading: loadingConnections,
    isFetching: fetchingConnections,
  } = useSavedConnectionsQuery(graphQLClient, org ? { org_domain: org } : {});
  const noConnections = !connectionList?.listConnections?.length;

  const connectionIdx = useLandingConnectionIdx();
  const parsedIndex = parseInt(connectionIdx, 10);
  const currentIndex = Number.isNaN(parsedIndex) ? -1 : parsedIndex;
  const foundConnection =
    currentIndex !== DEMO_CONNECTION_INDEX && currentIndex !== -1
      ? connectionList?.listConnections?.find((c) => c?.index === currentIndex)
      : null;
  const {
    connection: demoConnection,
    isLoading: loadingDemoConnection,
  } = useCurrentConnection(
    currentIndex === DEMO_CONNECTION_INDEX
      ? DEMO_CONNECTION_INDEX.toString()
      : '-1',
    org
  );
  const currentConnection =
    currentIndex === DEMO_CONNECTION_INDEX ? demoConnection : foundConnection;
  const isMissingConnection = currentIndex !== -1 && !currentConnection;

  React.useEffect(() => {
    if (queryParams.has('upgradeSuccess')) {
      history.replace(history.location.pathname);
      withConfirmationDs({
        isCloseButtonShown: false,
        message: copy.components.welcomeToEssentialsDialog.body,
        noPrompt: null,
        onConfirmationSuccess: noop,
        title: copy.components.welcomeToEssentialsDialog.title,
        yesPrompt: copy.components.welcomeToEssentialsDialog.buttonText,
      });
    }
  }, [history, queryParams]);

  React.useEffect(() => {
    // wait for the connections query to settle (incl. background refetches, e.g.
    // right after a connection is created) before treating the url as stale.
    if (
      !loadingConnections &&
      !fetchingConnections &&
      !loadingDemoConnection &&
      isMissingConnection
    ) {
      history.push(buildRoute(org, ''));
    }
  }, [
    fetchingConnections,
    history,
    isMissingConnection,
    loadingConnections,
    loadingDemoConnection,
    org,
  ]);

  // set current page as last visited connection index
  useSetLastConnectionIndex(currentConnection?.index, org);

  if (loadingConnections || loadingDemoConnection || isMissingConnection) {
    return null;
  }

  if (noConnections) {
    return <NewUserLanding />;
  }

  return (
    <>
      <Welcome />
      {currentConnection ? (
        <div>
          {isBrowserAuthFailure ? (
            <BrowserAuthFailedWarning index={currentIndex} org={org} />
          ) : null}
          {isDegraded ? (
            <DegradedWarning index={currentIndex} org={org} />
          ) : null}
          {ssoErrorCode ? <SSOWarning errorCode={ssoErrorCode} /> : null}
          {isUnauthorized ? (
            <UnauthorizedWarning index={currentIndex} org={org} />
          ) : null}
          <div className="sd-landing-dashboard">
            <H4 className="sd-landing-header">{currentConnection.name}</H4>
            <Dashboard index={currentIndex} />
          </div>
        </div>
      ) : null}
    </>
  );
};
